import { concat, compose, compact, last, reduce, sortBy, uniq, map } from 'lodash/fp';
import { keyById } from '@mews-commander/utils';
import { ResourceUseCluster } from '@mews-commander/legacy-entities/src/ducks/resourcesTree';

type UtcToUnit = (x: string) => number;

const concatUniqProperty = (property: keyof ResourceUseCluster) => (a: ResourceUseCluster, b: ResourceUseCluster) =>
    uniq(concat(a[property], b[property]));

const mergeClusters = (superCluster: ResourceUseCluster, cluster: ResourceUseCluster) => {
    const serviceIds = concatUniqProperty('serviceIds')(superCluster, cluster);
    const serviceNames = concatUniqProperty('serviceNames')(superCluster, cluster);
    const resourceUseIds = concatUniqProperty('resourceUseIds')(superCluster, cluster);
    const isMultipleServices = serviceIds.length > 1;
    return {
        ...superCluster,
        isMultipleServices,
        resourceUseIds,
        serviceIds,
        serviceNames,
        startUtc: superCluster.startUtc < cluster.startUtc ? superCluster.startUtc : cluster.startUtc,
        endUtc: superCluster.endUtc > cluster.endUtc ? superCluster.endUtc : cluster.endUtc,
        minX: null,
        maxX: null,
    } as ResourceUseCluster;
};

const doClustersOverlap = (a: ResourceUseCluster, b: ResourceUseCluster) =>
    a.startUtc <= b.endUtc && b.startUtc <= a.endUtc;
const areClustersFromSameTree = (a: ResourceUseCluster, b: ResourceUseCluster) =>
    a.assignedResourceId === b.assignedResourceId;

const mergeClustersIfOverlap = (a: ResourceUseCluster, b: ResourceUseCluster) => {
    if (a && b && areClustersFromSameTree(a, b) && doClustersOverlap(a, b)) {
        return [mergeClusters(a, b)];
    }
    return [a, b];
};

const updateClusterSizes = (utcToUnit: UtcToUnit) => (cluster: ResourceUseCluster) => {
    if (!cluster.minX || !cluster.maxX) {
        cluster.minX = utcToUnit(cluster.startUtc);
        cluster.maxX = utcToUnit(cluster.endUtc);
    }
    return cluster;
};

export const normalizeClusters = (allClusters: ResourceUseCluster[], utcToUnit: UtcToUnit) =>
    compose(
        keyById,
        map<ResourceUseCluster, ResourceUseCluster>(updateClusterSizes(utcToUnit)),
        compact,
        reduce<ResourceUseCluster, ResourceUseCluster[]>((clusters, cluster) => {
            const lastCluster = last<ResourceUseCluster>(clusters);
            const merged = lastCluster ? mergeClustersIfOverlap(lastCluster, cluster) : [cluster];
            return concat(clusters, merged);
        }, []),
        sortBy<ResourceUseCluster>(['assignedResourceId', 'startUtc'])
    )(allClusters);
